import * as React from "react";
import { useEffect, useState } from "react";
import { DateHelper } from "../../../../../react-core/Helpers/DateHelper";
import { MoneyDisplayHelper } from "../../../../../react-core/Helpers/MoneyDisplayHelper";
import { addDays, addYears } from "../../../../../react-core/utils/date";
import { CurrencyCode } from "../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { ServiceProposition } from "../../../../../Wrap/Constants/Enums/ServiceProposition";
import { AccountCreationHistoryModel } from "../../../../../Wrap/Models/Domains/Account/AccountCreationHistoryModel";
import { ClientSummaryGroupModel } from "../../../../../Wrap/Models/Models/Business/ClientSummary/ClientSummaryGroupModel";
import { Alert } from "../../../../Shared/Components/Alert";
import { AlertType } from "../../../../Shared/Entities/Constants/AlertConstants";
import AdviserSiteTagManager, {
    eventCategoryForGTM,
} from "../../../../Shared/Cookies/ChoicesCookie/AdviserSiteTagManager";
import { LoadingSkeletonType } from "../../../../Shared/LoadingSkeleton/Contents";
import { LoadingSkeletonWrapper } from "../../../../Shared/LoadingSkeleton/LoadingSkeletonWrapper";
import { getQueryParameters } from "../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { BusinessManagementAction } from "../Action/BusinessManagementAction";
import { ClientSummaryChart } from "./ClientSummary/ClientSummaryChart";
import { ClientSummaryTable } from "./ClientSummary/ClientSummaryTable";
import { NewAccountsChart } from "./ClientSummary/NewAccountsChart";
import { AdviserDropdown } from "./Search/AdviserDropdown";
import { UserStore } from "../../../Stroes/Variable";
import { JsonDateParser } from "../../../../../react-core/Utils/JsonDateParser";
import { CommonHelper } from "../../../../../react-core/Helpers/CommonHelper";
import { DateOptions } from "../../../../Shared/Components/Resource/Contents";

interface IClientSummaryProps {
    selectedProposition: ServiceProposition[];
}

export const ClientSummary = (props: IClientSummaryProps) => {
    const [data, setData] = useState<ClientSummaryGroupModel[]>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoading2, setIsLoading2] = useState(false);
    const [startGroup, setStartGroup] = useState({ GroupStartValue: [] });
    const [query, setQuery] = useState<{ hierarchyId: string; asAt: Date }>({
        hierarchyId: getQueryParameters()["firm"],
        asAt: addDays(new Date(), -1),
    });
    const [newAccounts, setNewAccounts] = useState<AccountCreationHistoryModel[]>(null);

    useEffect(() => {
        search(null, addDays(new Date(), -1));
        newAccountSearch(null, addYears(new Date(), -1), addDays(new Date(), -1));
    }, [props.selectedProposition]);

    useEffect(() => {
        addEvent({
            environment: {
                appType: "web",
                setting: "prod",
                timestamps: JsonDateParser.toJsonDateNoOffset(new Date()).toString(),
            },
            page: {
                pageUrl: window.location.pathname.includes("clientsummary")
                    ? window.location.pathname
                    : window.location.pathname + "/clientsummary",
                pageTitle: `${document.title} - business management - client summary`,
            },
            user: {
                loginId: UserStore.getUserInfo()?.ClientId.toString(),
                internal: "yes",
            },
            event: "pageLoaded",
        });
    }, []);

    const { addEvent } = new AdviserSiteTagManager();

    const filterData = (d) => {
        const data = d.map((ele: ClientSummaryGroupModel, index) => {
            const label = d[index + 1]
                ? `${MoneyDisplayHelper.FromatMoneyDisplay(
                      ele.GroupStartValue,
                      CurrencyCode.GBP
                  )} - ${MoneyDisplayHelper.FromatMoneyDisplay(d[index + 1].GroupStartValue, CurrencyCode.GBP)}`
                : `> ${MoneyDisplayHelper.FromatMoneyDisplay(ele.GroupStartValue, CurrencyCode.GBP)}`;
            return {
                GroupStartValue: label,
                Count: Number(ele.Count.toFixed(2)),
                Accounts: ele.Accounts,
            };
        });
        setStartGroup({ GroupStartValue: d.map((ele) => ele.GroupStartValue) });
        setIsLoading(false);
        setData(data);
    };

    const search = (advisor, asAt) => {
        setIsLoading(true);
        let hierarchyId = getQueryParameters()["firm"];
        if (advisor) {
            hierarchyId = advisor.HierarchyId;
        }
        setQuery({ hierarchyId: hierarchyId, asAt: asAt });
        BusinessManagementAction.getClientSummary(hierarchyId, asAt, props.selectedProposition, (d) => {
            filterData(d);
        });
    };

    const setSetting = (settings) => {
        setIsLoading(true);
        BusinessManagementAction.setClientSummary(
            settings,
            query.hierarchyId,
            query.asAt,
            props.selectedProposition,
            (d) => {
                filterData(d);
            }
        );
    };

    const newAccountSearch = (advisor, from, to) => {
        setIsLoading2(true);
        let hierarchyId = getQueryParameters()["firm"];
        if (advisor) {
            hierarchyId = advisor.HierarchyId;
        }
        BusinessManagementAction.getNewAccounts(
            hierarchyId,
            DateHelper.toStartOfDay(from),
            DateHelper.toStartOfDay(to),
            props.selectedProposition,
            (d) => {
                setNewAccounts(d);
                setIsLoading2(false);
            }
        );
    };

    return (
        <div id={"panel-Client summary"} role="tabpanel" tabIndex={0} aria-labelledby={"tab-Client summary"}>
            <h3>All Account statuses will be included in the figures below.</h3>
            <h2>Account summary</h2>
            <AdviserDropdown
                onSearch={(advisor, asAt) => {
                    addEvent({
                        event: "clientsummarytab_interaction",
                        eventParameters: {
                            eventAction: "interactions",
                            eventCategory: eventCategoryForGTM.clientSummaryTab,
                            eventLabel: `submit - date widget`,
                        },
                    });
                    search(advisor, asAt);
                }}
                isFromTo={false}
            />
            <LoadingSkeletonWrapper isLoading={isLoading} skeletonType={LoadingSkeletonType.PieChartAndTable}>
                {data == null ? null : data.length == 0 ? (
                    <Alert
                        alertType={AlertType.Warning}
                        message={"No results have been found for the entered criteria."}
                    />
                ) : (
                    <div className="panel-subtle container">
                        <div className="row">
                            <div className="col-12 col-sm-4">
                                <div className="account-validation-pie-chart mt-12">
                                    <ClientSummaryChart data={data} />
                                </div>
                            </div>
                            <div className="col-12 col-sm-8">
                                {
                                    <ClientSummaryTable
                                        data={data}
                                        saveSetting={(setting) => setSetting(setting)}
                                        setting={startGroup}
                                    />
                                }
                            </div>
                        </div>
                    </div>
                )}
            </LoadingSkeletonWrapper>
            <hr className="thicker" />
            <h2>New accounts</h2>
            <AdviserDropdown
                onSearch={(advisor, from, to, dateSelected) => {
                    addEvent({
                        event: "clientsummarytab_interaction",
                        eventParameters: {
                            eventAction: "interactions",
                            eventCategory: eventCategoryForGTM.clientSummaryTab,
                            eventLabel: `submit - ${CommonHelper.getOptionsTxt(
                                dateSelected,
                                DateOptions
                            ).toLowerCase()} - client summary`,
                        },
                    });
                    newAccountSearch(advisor, from, to);
                }}
                isFromTo
            />
            <LoadingSkeletonWrapper isLoading={isLoading2} skeletonType={LoadingSkeletonType.Chart}>
                <div className="panel-subtle">
                    {newAccounts == null ? null : newAccounts.length == 0 ? (
                        <Alert
                            alertType={AlertType.Warning}
                            message={"No results have been found for the entered criteria."}
                        />
                    ) : (
                        <NewAccountsChart data={newAccounts} />
                    )}
                </div>
            </LoadingSkeletonWrapper>
        </div>
    );
};
